import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, Video, Bell, BellOff, Users, Plus, X,
  Upload, ExternalLink, ChevronLeft, ChevronRight, Check
} from 'lucide-react';
import AppShell from '../components/AppShell';
import { FadeUp } from '../components/Animations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', event_time: '', meeting_link: '', meeting_platform: 'zoom' });
  const [bannerFile, setBannerFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => { loadEvents(); }, []);
  const loadEvents = async () => {
    try { const r = await axios.get(`${API}/events`); setEvents(r.data); } catch(e) {}
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.title || !form.event_date) { toast.error('Title and date required'); return; }
    setCreating(true);
    try {
      let bannerUrl = '';
      if (bannerFile) {
        const fd = new FormData();
        fd.append('file', bannerFile);
        const up = await axios.post(`${API}/upload/photo`, fd);
        bannerUrl = up.data.url;
      }
      await axios.post(`${API}/events`, { ...form, banner_url: bannerUrl });
      toast.success('Event created!');
      setShowCreate(false);
      setForm({ title: '', description: '', event_date: '', event_time: '', meeting_link: '', meeting_platform: 'zoom' });
      setBannerFile(null);
      loadEvents();
    } catch(e) { toast.error('Failed to create event'); }
    setCreating(false);
  };

  const handleRegister = async (eventId) => {
    try {
      const r = await axios.post(`${API}/events/${eventId}/register`, { user_id: user.user_id });
      if (r.data.status === 'already_registered') toast.info('Already registered');
      else toast.success('Registered for event!');
      loadEvents();
    } catch(e) { toast.error('Failed'); }
  };

  const handleNotify = async (eventId) => {
    try {
      const r = await axios.post(`${API}/events/${eventId}/notify`, { user_id: user.user_id });
      toast.success(r.data.status === 'on' ? 'Notifications on' : 'Notifications off');
      loadEvents();
    } catch(e) { toast.error('Failed'); }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try { await axios.delete(`${API}/events/${eventId}`); toast.success('Deleted'); loadEvents(); } catch(e) {}
  };

  // Calendar helpers
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

  const eventsByDate = {};
  events.forEach(ev => {
    const d = ev.event_date?.split('T')[0];
    if (d) { if (!eventsByDate[d]) eventsByDate[d] = []; eventsByDate[d].push(ev); }
  });

  const today = new Date().toISOString().split('T')[0];
  const upcoming = events.filter(e => e.event_date >= today).slice(0, 10);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <FadeUp>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="events-title">Live Events</h1>
              <p className="text-[#A39BB0] font-inter text-sm">Monthly strategy calls, workshops, and community events</p>
            </div>
            {isAdmin && (
              <button onClick={() => setShowCreate(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold text-sm px-5 py-2.5 rounded-full"
                data-testid="create-event-btn">
                <Plus className="w-4 h-4" /><span>Create Event</span>
              </button>
            )}
          </div>
        </FadeUp>

        {/* Admin Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCreate(false)}>
            <div className="bg-[#1A0533] rounded-2xl border border-white/[0.1] p-8 max-w-lg w-full my-8" onClick={e => e.stopPropagation()} data-testid="create-event-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-cormorant font-bold text-white">Create Event</h2>
                <button onClick={() => setShowCreate(false)} className="text-[#A39BB0] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-[#A39BB0] text-xs font-inter mb-1">Event Banner (YouTube banner size)</label>
                  <div className="border-2 border-dashed border-white/[0.1] rounded-xl overflow-hidden">
                    {bannerFile ? (
                      <div className="relative">
                        <img src={URL.createObjectURL(bannerFile)} alt="" className="w-full h-40 object-cover" />
                        <button onClick={() => setBannerFile(null)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block p-8 text-center">
                        <Upload className="w-8 h-8 text-white/20 mx-auto mb-2" />
                        <p className="text-[#A39BB0] text-sm font-inter">Upload event banner image</p>
                        <input type="file" accept="image/*" className="hidden" onChange={e => setBannerFile(e.target.files[0])} data-testid="event-banner-input" />
                      </label>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-[#A39BB0] text-xs font-inter mb-1">Title</label>
                  <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                    placeholder="Monthly Power Strategy Call" data-testid="event-title-input" />
                </div>
                <div>
                  <label className="block text-[#A39BB0] text-xs font-inter mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none min-h-[60px] resize-none"
                    placeholder="What we'll cover..." data-testid="event-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#A39BB0] text-xs font-inter mb-1">Date</label>
                    <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})}
                      className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                      data-testid="event-date-input" />
                  </div>
                  <div>
                    <label className="block text-[#A39BB0] text-xs font-inter mb-1">Time</label>
                    <input type="time" value={form.event_time} onChange={e => setForm({...form, event_time: e.target.value})}
                      className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                      data-testid="event-time-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#A39BB0] text-xs font-inter mb-1">Platform</label>
                  <div className="flex space-x-2">
                    {['zoom', 'riverside', 'google_meet', 'other'].map(p => (
                      <button key={p} onClick={() => setForm({...form, meeting_platform: p})}
                        className={`px-3 py-1.5 rounded-full text-xs font-inter font-medium capitalize ${form.meeting_platform === p ? 'bg-purple-600 text-white' : 'bg-white/[0.05] text-[#A39BB0] border border-white/[0.1]'}`}>
                        {p.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[#A39BB0] text-xs font-inter mb-1">Meeting Link</label>
                  <input value={form.meeting_link} onChange={e => setForm({...form, meeting_link: e.target.value})}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                    placeholder="https://zoom.us/j/..." data-testid="event-link-input" />
                </div>
                <button onClick={handleCreate} disabled={creating || !form.title || !form.event_date}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold py-3 rounded-full disabled:opacity-50"
                  data-testid="event-create-submit">{creating ? 'Creating...' : 'Create Event'}</button>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Upcoming Events */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-12 text-[#A39BB0] font-inter">Loading events...</div>
            ) : upcoming.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-12 h-12 text-white/15 mx-auto mb-4" />
                <h3 className="text-xl font-cormorant font-bold text-white mb-2">No Upcoming Events</h3>
                <p className="text-[#A39BB0] font-inter text-sm">Check back soon for live strategy calls and workshops</p>
              </div>
            ) : (
              upcoming.map(event => {
                const isRegistered = (event.registered_users || []).includes(user.user_id);
                const hasNotify = (event.notify_users || []).includes(user.user_id);
                const eventDate = new Date(event.event_date + 'T' + (event.event_time || '00:00'));
                const isPast = eventDate < new Date();
                return (
                  <FadeUp key={event.event_id}>
                    <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] overflow-hidden" data-testid={`event-${event.event_id}`}>
                      {/* Banner */}
                      {event.banner_url && (
                        <div className="relative">
                          <img src={event.banner_url.startsWith('http') ? event.banner_url : `${BACKEND_URL}${event.banner_url}`}
                            alt={event.title} className="w-full h-48 sm:h-56 object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#1A0533]/80 to-transparent" />
                          <div className="absolute bottom-4 left-5">
                            <div className="bg-[#D4AF37] text-black font-inter font-bold text-xs px-3 py-1 rounded-full inline-flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>{eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h2 className="text-xl font-cormorant font-bold text-white mb-1">{event.title}</h2>
                            <div className="flex items-center space-x-3 text-[#A39BB0] text-xs font-inter">
                              <span className="flex items-center space-x-1"><Clock className="w-3 h-3" /><span>{event.event_time || 'TBD'} {event.timezone}</span></span>
                              <span className="flex items-center space-x-1"><Video className="w-3 h-3" /><span className="capitalize">{event.meeting_platform?.replace('_',' ')}</span></span>
                              <span className="flex items-center space-x-1"><Users className="w-3 h-3" /><span>{(event.registered_users || []).length} registered</span></span>
                            </div>
                          </div>
                          {isAdmin && (
                            <button onClick={() => handleDelete(event.event_id)} className="text-[#A39BB0] hover:text-red-400 text-xs font-inter">Delete</button>
                          )}
                        </div>
                        {event.description && <p className="text-[#A39BB0] font-inter text-sm leading-relaxed mb-4">{event.description}</p>}
                        {!event.banner_url && (
                          <div className="text-[#A39BB0] text-xs font-inter mb-3">
                            {eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                        <div className="flex items-center space-x-3">
                          {!isPast && (
                            <>
                              <motion.button onClick={() => handleRegister(event.event_id)}
                                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                className={`flex items-center space-x-2 font-inter font-bold text-sm px-5 py-2.5 rounded-full transition-all ${
                                  isRegistered ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                                }`} data-testid={`register-event-${event.event_id}`}>
                                {isRegistered ? <><Check className="w-4 h-4" /><span>Registered</span></> : <><Calendar className="w-4 h-4" /><span>Register</span></>}
                              </motion.button>
                              <button onClick={() => handleNotify(event.event_id)}
                                className={`flex items-center space-x-1.5 text-xs font-inter px-3 py-2 rounded-full border transition-all ${
                                  hasNotify ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-white/[0.05] text-[#A39BB0] border-white/[0.08] hover:text-white'
                                }`} data-testid={`notify-event-${event.event_id}`}>
                                {hasNotify ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                                <span>{hasNotify ? 'Notified' : 'Notify me'}</span>
                              </button>
                            </>
                          )}
                          {isRegistered && event.meeting_link && (
                            <a href={event.meeting_link} target="_blank" rel="noreferrer"
                              className="flex items-center space-x-1.5 text-purple-300 hover:text-white text-xs font-inter px-3 py-2 rounded-full bg-white/[0.05] border border-white/[0.08] hover:bg-white/10 transition-all"
                              data-testid={`join-event-${event.event_id}`}>
                              <ExternalLink className="w-3.5 h-3.5" /><span>Join Call</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </FadeUp>
                );
              })
            )}
          </div>

          {/* Right: Calendar Sidebar */}
          <div className="space-y-4">
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid="events-calendar">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
                  className="text-[#A39BB0] hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
                <h3 className="text-white font-inter font-semibold text-sm">{MONTHS[calMonth]} {calYear}</h3>
                <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
                  className="text-[#A39BB0] hover:text-white"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {['S','M','T','W','T','F','S'].map((d,i) => (
                  <div key={i} className="text-[#A39BB0] text-[10px] font-inter font-bold py-1">{d}</div>
                ))}
                {calDays.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} />;
                  const dateStr = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const hasEvent = eventsByDate[dateStr];
                  const isToday = dateStr === today;
                  return (
                    <div key={i} className={`py-1.5 rounded-lg text-xs font-inter relative cursor-default ${
                      isToday ? 'bg-purple-600 text-white font-bold' : hasEvent ? 'bg-amber-500/20 text-amber-400 font-semibold' : 'text-[#A39BB0]'
                    }`}>
                      {day}
                      {hasEvent && <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-400" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* My Registered Events */}
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5">
              <h3 className="text-sm font-inter font-semibold text-white mb-3">My Registered Events</h3>
              {events.filter(e => (e.registered_users || []).includes(user.user_id)).length === 0 ? (
                <p className="text-[#A39BB0] text-xs font-inter">No registered events yet</p>
              ) : (
                <div className="space-y-2">
                  {events.filter(e => (e.registered_users || []).includes(user.user_id)).map(e => (
                    <div key={e.event_id} className="flex items-center space-x-3 p-2 bg-white/[0.03] rounded-lg">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-purple-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs font-inter font-medium truncate">{e.title}</p>
                        <p className="text-[#A39BB0] text-[10px] font-inter">{new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {e.event_time || 'TBD'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
